import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { TailoredResumeWizard } from '../components/TailoredResumeWizard'
import { useResumes } from '../hooks/useResumes'
import { jobApplicationService, ResumeFitAnalysisResponse } from '../services/jobApplicationService'
import { LoadingSpinner } from '../components/LoadingSpinner'
import './TailoredResumeWizardPage.css'

export const TailoredResumeWizardPage: React.FC = () => {
  const { jobApplicationId } = useParams<{ jobApplicationId: string }>()
  const navigate = useNavigate()
  const resumesHook = useResumes()
  const [fitAnalysis, setFitAnalysis] = useState<ResumeFitAnalysisResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Gate: no resumes -> redirect to upload (plan: "Resume required — upload one to continue.")
  useEffect(() => {
    if (resumesHook.isLoading) return
    if ((resumesHook.resumes?.length ?? 0) === 0) {
      navigate('/resumes', { replace: true })
    }
  }, [resumesHook.isLoading, resumesHook.resumes?.length, navigate])

  useEffect(() => {
    if (!jobApplicationId) {
      setError('Job application ID is required')
      setIsLoading(false)
      return
    }

    const loadData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Load fit analysis
        const analysis = await jobApplicationService.getResumeFitAnalysis(jobApplicationId)
        setFitAnalysis(analysis)
      } catch (err: any) {
        console.error('Failed to load wizard data:', err)
        setError(err.message || 'Failed to load data')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [jobApplicationId])

  const handleClose = () => {
    // Close the window
    window.close()
    // If window.close() doesn't work (some browsers block it), navigate back
    if (!document.hidden) {
      navigate('/applications')
    }
  }

  const handleResumeGenerated = () => {
    // Notify parent window if it exists
    if (window.opener) {
      window.opener.postMessage({ type: 'tailored-resume-generated', jobApplicationId }, '*')
    }
    // Close the window
    handleClose()
  }

  if (isLoading) {
    return (
      <div className="tailored-resume-wizard-page">
        <div className="loading-container">
          <LoadingSpinner size="large" />
          <p>Loading wizard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="tailored-resume-wizard-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleClose}>Close</button>
        </div>
      </div>
    )
  }

  if (!jobApplicationId || !fitAnalysis) {
    return (
      <div className="tailored-resume-wizard-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>Missing required data</p>
          <button onClick={handleClose}>Close</button>
        </div>
      </div>
    )
  }

  return (
    <div className="tailored-resume-wizard-page">
      <TailoredResumeWizard
        isOpen={true}
        onClose={handleClose}
        jobApplicationId={jobApplicationId}
        fitAnalysis={fitAnalysis}
        availableResumes={resumesHook.resumes}
        onResumeGenerated={handleResumeGenerated}
      />
    </div>
  )
}

